import { Head, Link, useForm } from '@inertiajs/react'
import { FormEvent } from 'react'
import {
  Box,
  Button,
  Callout,
  Checkbox,
  Flex,
  Grid,
  Heading,
  Select,
  Separator,
  Text,
  TextField,
} from '@radix-ui/themes'
import { AlertCircle, Camera, Globe, Info, Instagram, Mail, MapPinHouse, Phone, User, Youtube } from 'lucide-react'
import CredentialGatePicker from '@/shared/credentials/CredentialGatePicker'
import {
  EventApplyProps,
  EventApplyRole,
  EventApplyMediaGroupFields,
  EventApplyParticipantFields,
  EventApplyStaffFields,
} from '@/types/event_apply'

type FormState = {
  roles: EventApplyRole[]
  participant: EventApplyParticipantFields
  media_group: EventApplyMediaGroupFields
  staff: EventApplyStaffFields
  credential_category: string | null
}

export default function EventApply({
  event,
  submit_path,
  event_path,
  participant,
  media_group,
  staff,
  selected_roles,
  credential_gate,
  credential_category,
  errors,
}: EventApplyProps) {
  const { data, setData, post, processing } = useForm<FormState>({
    roles: selected_roles ?? [],
    participant,
    // The listing select shows Photography until changed, so send what it shows.
    media_group: { listing: 'photography', ...media_group },
    staff,
    credential_category,
  })

  const setParticipant = (fields: Partial<EventApplyParticipantFields>) =>
    setData('participant', { ...data.participant, ...fields })
  const setMediaGroup = (fields: Partial<EventApplyMediaGroupFields>) =>
    setData('media_group', { ...data.media_group, ...fields })
  const setStaff = (fields: Partial<EventApplyStaffFields>) =>
    setData('staff', { ...data.staff, ...fields })

  const toggleRole = (role: EventApplyRole, on: boolean) => {
    const next = on ? Array.from(new Set([...data.roles, role])) : data.roles.filter((r) => r !== role)
    setData('roles', next)
  }

  const showMedia = data.roles.includes('media')
  const showStaff = data.roles.includes('staff')

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(submit_path)
  }

  return (
    <>
      <Head title={`Apply to ${event.name}`} />
      <Box style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <Box className="border-2 py-8 px-10">
          <Heading size="7">Apply to {event.name}</Heading>
          <Text size="4" as="p" color="gray">
            Anyone can apply — you don't need an account. We'll email you a link to view or withdraw later.
          </Text>
          <Separator my="3" size="4" />

          {errors && errors.length > 0 && (
            <Callout.Root color="red" role="alert" my="3">
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>
                {errors.map((error, i) => (
                  <Box key={i}>{error}</Box>
                ))}
              </Callout.Text>
            </Callout.Root>
          )}

          <form onSubmit={handleSubmit}>
            <Text size="6">What are you applying as?</Text>
            <Separator my="3" size="4" />
            <Flex direction="column" gap="3" my="3">
              {event.accepts_media && (
                <Text as="label" size="3">
                  <Flex align="center" gap="2">
                    <Checkbox checked={showMedia} onCheckedChange={(c) => toggleRole('media', c === true)} />
                    <Text size="4">Media (photographer / videographer)</Text>
                  </Flex>
                </Text>
              )}
              {event.accepts_staff && (
                <Text as="label" size="3">
                  <Flex align="center" gap="2">
                    <Checkbox checked={showStaff} onCheckedChange={(c) => toggleRole('staff', c === true)} />
                    <Text size="4">Staff (event crew / gate / cashier)</Text>
                  </Flex>
                </Text>
              )}
              {!event.accepts_media && !event.accepts_staff && (
                <Text color="gray">This event isn't accepting media or staff applications right now.</Text>
              )}
            </Flex>

            {showMedia && credential_gate && (
              <Flex direction="column" gap="3" my="5">
                <CredentialGatePicker
                  gate={credential_gate}
                  value={data.credential_category}
                  onChange={(category) => setData('credential_category', category)}
                />
                <Callout.Root color="gray" variant="soft" size="2">
                  <Callout.Icon>
                    <Info size={18} />
                  </Callout.Icon>
                  <Callout.Text>
                    Haven't filled out {credential_gate.track_name}'s annual form for this yet — or has it
                    expired? We'll take you there when you submit, then bring you straight back with
                    everything you've typed still here.
                  </Callout.Text>
                </Callout.Root>
              </Flex>
            )}

            {data.roles.length > 0 && (
              <>
                <Text size="6">Your contact info</Text>
                <Separator my="3" size="4" />
                <Grid columns="1" gap="5" my="5">
                  <LabeledField label="Name *">
                    <TextField.Root
                      size="3"
                      value={data.participant.name ?? ''}
                      onChange={(e) => setParticipant({ name: e.target.value })}
                      required
                      placeholder="Full name"
                    >
                      <TextField.Slot>
                        <User height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                  </LabeledField>
                  <LabeledField label="Email *">
                    <TextField.Root
                      type="email"
                      size="3"
                      value={data.participant.email ?? ''}
                      onChange={(e) => setParticipant({ email: e.target.value })}
                      required
                      placeholder="you@example.com"
                    >
                      <TextField.Slot>
                        <Mail height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                  </LabeledField>
                  <LabeledField label="Phone Number">
                    <TextField.Root
                      size="3"
                      value={data.participant.phone_number ?? ''}
                      onChange={(e) => setParticipant({ phone_number: e.target.value })}
                      placeholder="Optional"
                    >
                      <TextField.Slot>
                        <Phone height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                  </LabeledField>
                  <LabeledField label="Location">
                    <TextField.Root
                      size="3"
                      value={data.participant.location ?? ''}
                      onChange={(e) => setParticipant({ location: e.target.value })}
                      placeholder="Where are you based?"
                    >
                      <TextField.Slot>
                        <MapPinHouse height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                  </LabeledField>
                  <Grid columns={{ initial: '1', sm: '3' }} gap="5">
                    <LabeledField label="Instagram">
                      <TextField.Root
                        size="3"
                        value={data.participant.instagram ?? ''}
                        onChange={(e) => setParticipant({ instagram: e.target.value })}
                        placeholder="@handle"
                      >
                        <TextField.Slot>
                          <Instagram height="16" width="16" />
                        </TextField.Slot>
                      </TextField.Root>
                    </LabeledField>
                    <LabeledField label="YouTube">
                      <TextField.Root
                        size="3"
                        value={data.participant.youtube ?? ''}
                        onChange={(e) => setParticipant({ youtube: e.target.value })}
                        placeholder="Channel name or URL"
                      >
                        <TextField.Slot>
                          <Youtube height="16" width="16" />
                        </TextField.Slot>
                      </TextField.Root>
                    </LabeledField>
                    <LabeledField label="Flickr">
                      <TextField.Root
                        size="3"
                        value={data.participant.flickr ?? ''}
                        onChange={(e) => setParticipant({ flickr: e.target.value })}
                        placeholder="Username or URL"
                      >
                        <TextField.Slot>
                          <Camera height="16" width="16" />
                        </TextField.Slot>
                      </TextField.Root>
                    </LabeledField>
                  </Grid>
                </Grid>
              </>
            )}

            {showMedia && (
              <>
                <Text size="6">Media details</Text>
                <Separator my="3" size="4" />
                <Grid columns={{ initial: '1', sm: '2' }} gap="5" my="5">
                  <LabeledField label="Primary Outlet">
                    <Select.Root
                      size="3"
                      value={data.media_group.primary_outlet ?? ''}
                      onValueChange={(v) => setMediaGroup({ primary_outlet: v })}
                    >
                      <Select.Trigger placeholder="Select your main platform..." />
                      <Select.Content>
                        <Select.Item value="instagram">Instagram</Select.Item>
                        <Select.Item value="youtube">YouTube</Select.Item>
                        <Select.Item value="flickr">Flickr</Select.Item>
                        <Select.Item value="website">Personal Website</Select.Item>
                        <Select.Item value="not_applicable">Not applicable</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </LabeledField>
                  <LabeledField label="List me under *">
                    <Select.Root
                      size="3"
                      value={data.media_group.listing}
                      onValueChange={(v) => setMediaGroup({ listing: v })}
                      required
                    >
                      <Select.Trigger />
                      <Select.Content>
                        <Select.Item value="photography">Photography</Select.Item>
                        <Select.Item value="drone">Drone</Select.Item>
                        <Select.Item value="unlisted">Don't list me</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </LabeledField>
                  <LabeledField label="Portfolio Link">
                    <TextField.Root
                      type="url"
                      size="3"
                      value={data.media_group.portfolio_link ?? ''}
                      onChange={(e) => setMediaGroup({ portfolio_link: e.target.value })}
                      placeholder="https://..."
                    >
                      <TextField.Slot>
                        <Globe height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                  </LabeledField>
                </Grid>
              </>
            )}

            {showStaff && (
              <>
                <Text size="6">Staff details</Text>
                <Separator my="3" size="4" />
                <Grid columns="1" gap="5" my="5">
                  <LabeledField label="Events worked">
                    <TextField.Root
                      size="3"
                      value={data.staff.events_worked ?? ''}
                      onChange={(e) => setStaff({ events_worked: e.target.value })}
                      placeholder="Past events you've worked"
                    />
                  </LabeledField>
                  <LabeledField label="Cashier experience">
                    <TextField.Root
                      size="3"
                      value={data.staff.cashier_experience ?? ''}
                      onChange={(e) => setStaff({ cashier_experience: e.target.value })}
                      placeholder="Briefly describe"
                    />
                  </LabeledField>
                  <LabeledField label="Favored positions">
                    <TextField.Root
                      size="3"
                      value={data.staff.favored_positions ?? ''}
                      onChange={(e) => setStaff({ favored_positions: e.target.value })}
                      placeholder="Gate, grid, cashier, etc."
                    />
                  </LabeledField>
                  <Grid columns={{ initial: '1', sm: '3' }} gap="5">
                    <LabeledField label="Days available">
                      <TextField.Root
                        type="number"
                        min={0}
                        size="3"
                        value={data.staff.days_staffed ?? ''}
                        onChange={(e) => setStaff({ days_staffed: e.target.value === '' ? '' : Number(e.target.value) })}
                      />
                    </LabeledField>
                    <Box>
                      <Text size="3" as="label">
                        <Flex align="center" gap="2" mt="6">
                          <Checkbox
                            checked={!!data.staff.staffed_before}
                            onCheckedChange={(c) => setStaff({ staffed_before: c === true })}
                          />
                          <Text>Staffed an event before</Text>
                        </Flex>
                      </Text>
                    </Box>
                    <Box>
                      <Text size="3" as="label">
                        <Flex align="center" gap="2" mt="6">
                          <Checkbox
                            checked={!!data.staff.cashiered_before}
                            onCheckedChange={(c) => setStaff({ cashiered_before: c === true })}
                          />
                          <Text>Cashiered before</Text>
                        </Flex>
                      </Text>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}

            {/* The same terms an account holder agrees to. Plain anchors: the
                legal pages aren't Inertia pages. */}
            <Text as="p" size="2" color="gray" mt="8">
              By applying you agree to Driftables'{' '}
              <a href="/terms" className="text-accent11 underline">Terms of Use</a> and{' '}
              <a href="/privacy" className="text-accent11 underline">Privacy Policy</a>. The event's organizer sees
              what you send with your application.
            </Text>

            <Box mt="4">
              <Flex gap="4" justify="end">
                <Button type="button" size="3" asChild color="gray" variant="soft">
                  <Link href={event_path}>Cancel</Link>
                </Button>
                <Button size="3" type="submit" disabled={processing || data.roles.length === 0}>
                  Submit application
                </Button>
              </Flex>
            </Box>
          </form>
        </Box>
      </Box>
    </>
  )
}

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Text as="label" size="3">
        <Flex gap="2" direction="column">
          <Text size="5">{label}</Text>
          {children}
        </Flex>
      </Text>
    </Box>
  )
}
